// ═══════════════════════════════════════════════════════════════════════════════
// Align client UserProfile.pairedAt with server (GET /auth/me + fallback GET /pairing/status)
// ═══════════════════════════════════════════════════════════════════════════════

import { api } from './api';
import type { UserProfile } from '@fdh/shared';
import type { PairingStatusResponse } from '@fdh/shared';

const isPairedAt = (pairedAt: string | undefined): boolean =>
  Boolean(pairedAt && pairedAt.trim() !== '');

/**
 * Refreshes profile from /auth/me; if still unpaired, checks /pairing/status and patches
 * pairedAt when the server reports an active pairing (covers stale client cache edge cases).
 */
export const reconcilePairedUserProfile = async (
  refreshUser: () => Promise<UserProfile | null>,
  updateUser: (next: UserProfile) => void,
  currentUser: UserProfile
): Promise<UserProfile | null> => {
  const fresh = await refreshUser();
  const base = fresh ?? currentUser;

  if (isPairedAt(base.pairedAt)) {
    return base;
  }

  const statusRes = await api.get<PairingStatusResponse>('/pairing/status');
  if (
    !statusRes.success ||
    !statusRes.data?.isPaired ||
    !statusRes.data.pairing?.pairedAt ||
    !isPairedAt(statusRes.data.pairing.pairedAt)
  ) {
    return base;
  }

  const merged: UserProfile = { ...base, pairedAt: statusRes.data.pairing.pairedAt };
  updateUser(merged);
  return merged;
};
